import React, { useEffect, useState } from "react";
import { Loader2, Plus, Pencil, Trash2, Building2, Power, Code2, FormInput } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const slug = (s) => (s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const emptyForm = {
  id: "", name: "", tagline: "", website: "", notes: "", active: true,
  method: "portal", intake_email: "", portal_url: "", provider: "", automate: false, commit: false,
  instructions: "", _full: null,
};

function profileToForm(p) {
  const sub = p.submission || {};
  const portal = sub.portal || {};
  return {
    id: p.id || "", name: p.name || "", tagline: p.tagline || "", website: p.website || "",
    notes: p.notes || "", active: p.active !== false,
    method: sub.method || "portal", intake_email: sub.intake_email || "",
    portal_url: sub.portal_url || "", provider: portal.provider || "",
    automate: !!sub.automate, commit: !!portal.commit, instructions: sub.instructions || "",
    _full: p,
  };
}

function formToProfile(f) {
  const base = f._full ? JSON.parse(JSON.stringify(f._full)) : {};
  const id = (f.id && f.id.trim()) || slug(f.name);
  const profile = {
    ...base,
    id, name: f.name, tagline: f.tagline, website: f.website, notes: f.notes, active: f.active,
    states_excluded: base.states_excluded || [], msa_excluded: base.msa_excluded || [],
    products: base.products || [],
  };
  const sub = { method: f.method, portal_url: f.portal_url || "", instructions: f.instructions || "" };
  if (f.method === "email") sub.intake_email = f.intake_email || "";
  if (f.method === "portal") {
    sub.automate = !!f.automate;
    const existingPortal = (base.submission && base.submission.portal) || {};
    if (f.provider) {
      sub.portal = { ...existingPortal, provider: f.provider, login_url: f.portal_url || existingPortal.login_url || "", commit: !!f.commit };
    } else {
      sub.automate = false;
    }
  }
  profile.submission = sub;
  return profile;
}

const Field = ({ label, hint, children }) => (
  <div>
    <label className="text-xs uppercase tracking-wide text-faint">{label}</label>
    {children}
    {hint && <p className="text-[11px] text-dim mt-1">{hint}</p>}
  </div>
);

const inputCls = "mt-1 bg-obsidian border-edge text-white";

const AUTOMATIONS = [
  { value: "", label: "None — I'll submit in the portal myself" },
  { value: "liquidlogics", label: "LiquidLogics broker portal (e.g. Mino Lending)" },
  { value: "private_lender_portal", label: "Private Lender Portal (Finance of America)" },
  { value: "loanpass_dominion", label: "LoanPASS (Dominion Financial)" },
  { value: "backflip_gravity", label: "Backflip inquiry form" },
];

export default function AdminLenders() {
  const [list, setList] = useState(null);
  const [form, setForm] = useState(null);
  const [advanced, setAdvanced] = useState(false);
  const [json, setJson] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/lenders").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openEdit = (p) => { setAdvanced(false); setForm(profileToForm(p)); };
  const openNew = () => { setAdvanced(false); setForm({ ...emptyForm }); };
  const close = () => setForm(null);

  const toAdvanced = () => { setJson(JSON.stringify(formToProfile(form), null, 2)); setAdvanced(true); };
  const toSimple = () => {
    try { setForm(profileToForm(JSON.parse(json))); setAdvanced(false); }
    catch (e) { toast.error("Invalid JSON: " + e.message); }
  };

  const save = async () => {
    let profile;
    if (advanced) {
      try { profile = JSON.parse(json); } catch (e) { toast.error("Invalid JSON: " + e.message); return; }
    } else {
      if (!form.name || !form.name.trim()) { toast.error("Lender name is required"); return; }
      profile = formToProfile(form);
      if (!form._full && list.some((l) => l.id === profile.id)) {
        toast.error(`A lender named "${form.name}" already exists — edit that one instead`);
        return;
      }
    }
    if (!profile.id || !profile.id.trim()) { toast.error("Could not derive an id — enter a lender name"); return; }
    setSaving(true);
    try {
      await api.post("/lenders", { profile });
      toast.success("Lender saved");
      close(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleActive = async (p) => {
    try { await api.post("/lenders", { profile: { ...p, active: !(p.active !== false) } }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.name}? This cannot be undone.`)) return;
    try { await api.delete(`/lenders/${p.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (list === null) return <div className="py-20 grid place-items-center"><Loader2 className="w-8 h-8 text-gold animate-spin" /></div>;

  return (
    <div data-testid="admin-lenders">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="h-display text-3xl text-white">Lender Profiles</h1>
          <p className="mt-1 text-dim text-sm">Add lenders and the fee sheets that power Archer's matching engine.</p>
        </div>
        <Button onClick={openNew} className="btn-gold rounded-full h-11 px-5 gap-2" data-testid="lender-new"><Plus className="w-4 h-4" /> Add Lender</Button>
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        {list.map((p) => (
          <div key={p.id} className={`card-surface rounded-xl p-5 ${p.active === false ? "opacity-60" : ""}`} data-testid={`lender-card-${p.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-gold shrink-0" />
                  <span className="text-white font-bold text-lg truncate">{p.name}</span>
                  {p.active === false && <span className="text-[10px] uppercase tracking-wide text-faint border border-edge rounded px-1.5 py-0.5">Off</span>}
                  {(p.submission?.method === "portal") && <span className="text-[10px] uppercase tracking-wide text-gold/80 border border-gold/30 rounded px-1.5 py-0.5">Portal</span>}
                  {(p.submission?.method === "email") && <span className="text-[10px] uppercase tracking-wide text-gold/80 border border-gold/30 rounded px-1.5 py-0.5">Email</span>}
                </div>
                <div className="text-xs text-faint mt-0.5 truncate">{p.tagline}</div>
                <div className="text-xs text-dim mt-2">{(p.products || []).length} product(s){(p.products || []).length ? ": " + (p.products || []).map((x) => x.name).join(", ") : ""}</div>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2">
              <Button onClick={() => openEdit(p)} variant="outline" size="sm" className="btn-outline-glass rounded-lg gap-1" data-testid={`lender-edit-${p.id}`}><Pencil className="w-3.5 h-3.5" /> Edit</Button>
              <Button onClick={() => toggleActive(p)} variant="outline" size="sm" className="btn-outline-glass rounded-lg gap-1" data-testid={`lender-toggle-${p.id}`}><Power className="w-3.5 h-3.5" /> {p.active === false ? "Enable" : "Disable"}</Button>
              <Button onClick={() => remove(p)} variant="ghost" size="sm" className="text-brandred hover:text-brandred gap-1 ml-auto" data-testid={`lender-delete-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>

      {form && (
        <Dialog open onOpenChange={close}>
          <DialogContent className="bg-charcoal border-edge text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="lender-editor">
            <div className="flex items-center justify-between gap-3">
              <h2 className="h-display text-2xl text-white">{form.id ? `Edit ${form.name || form.id}` : "New Lender"}</h2>
              {advanced
                ? <Button variant="outline" size="sm" className="btn-outline-glass rounded-lg gap-1" onClick={toSimple} data-testid="lender-mode-simple"><FormInput className="w-3.5 h-3.5" /> Simple form</Button>
                : <Button variant="outline" size="sm" className="btn-outline-glass rounded-lg gap-1" onClick={toAdvanced} data-testid="lender-mode-advanced"><Code2 className="w-3.5 h-3.5" /> Advanced JSON</Button>}
            </div>

            {advanced ? (
              <>
                <p className="text-sm text-dim mt-1">Full structured profile. Products / rate &amp; leverage tiers drive the matching engine.</p>
                <Textarea value={json} onChange={(e) => setJson(e.target.value)} spellCheck={false}
                  className="mt-3 bg-obsidian border-edge text-white font-mono text-xs h-[55vh]" data-testid="lender-json" />
              </>
            ) : (
              <div className="mt-3 space-y-4">
                <p className="text-sm text-dim">Fill the basics — this is all you need to add a lender as a submission destination. Rate/leverage tiers for matching live under <span className="text-white/80">Advanced JSON</span>.</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Lender name" hint="Required">
                    <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Acme Capital" className={inputCls} data-testid="lender-field-name" />
                  </Field>
                  <Field label="Tagline">
                    <Input value={form.tagline} onChange={(e) => set("tagline", e.target.value)} placeholder="Business-purpose lending" className={inputCls} data-testid="lender-field-tagline" />
                  </Field>
                  <Field label="Website">
                    <Input value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="minolending.com" className={inputCls} data-testid="lender-field-website" />
                  </Field>
                  <Field label="Status">
                    <label className="mt-2 flex items-center gap-2 text-sm text-white/80 cursor-pointer">
                      <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} data-testid="lender-field-active" /> Active (available for matching &amp; submission)
                    </label>
                  </Field>
                </div>

                <Field label="How do we submit deals to this lender?">
                  <select value={form.method} onChange={(e) => set("method", e.target.value)}
                    className="mt-1 w-full h-10 rounded-md bg-obsidian border border-edge text-white px-3 text-sm" data-testid="lender-field-method">
                    <option value="portal">Lender portal</option>
                    <option value="email">Email the package</option>
                    <option value="none">Manual / prepare only</option>
                  </select>
                </Field>

                {form.method === "email" && (
                  <Field label="Intake email" hint="Where the deal package link is emailed.">
                    <Input value={form.intake_email} onChange={(e) => set("intake_email", e.target.value)} placeholder="submissions@lender.com" className={inputCls} data-testid="lender-field-intake-email" />
                  </Field>
                )}

                {form.method === "portal" && (
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Portal URL">
                      <Input value={form.portal_url} onChange={(e) => set("portal_url", e.target.value)} placeholder="https://portal.lender.com/login" className={inputCls} data-testid="lender-field-portal-url" />
                    </Field>
                    <Field label="Portal robot" hint="Pick the portal type so the agent can log in and fill the loan for you.">
                      <select value={form.provider} onChange={(e) => set("provider", e.target.value)}
                        className="mt-1 w-full h-10 rounded-md bg-obsidian border border-edge text-white px-3 text-sm" data-testid="lender-field-provider">
                        {AUTOMATIONS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                      </select>
                    </Field>
                    <div className="sm:col-span-2 flex flex-col gap-2">
                      <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer">
                        <input type="checkbox" checked={form.automate} onChange={(e) => set("automate", e.target.checked)} data-testid="lender-field-automate" /> Run the submission agent automatically
                      </label>
                      <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer">
                        <input type="checkbox" checked={form.commit} onChange={(e) => set("commit", e.target.checked)} data-testid="lender-field-commit" /> Commit live (uncheck for a safe dry-run — fills the form but does not submit)
                      </label>
                    </div>
                  </div>
                )}

                <Field label="Submission instructions / notes">
                  <Textarea value={form.instructions} onChange={(e) => set("instructions", e.target.value)} placeholder="Any manual steps or context for this lender." className={`${inputCls} h-20`} data-testid="lender-field-instructions" />
                </Field>
                <Field label="Internal notes">
                  <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} className={`${inputCls} h-16`} data-testid="lender-field-notes" />
                </Field>
              </div>
            )}

            <div className="mt-5 flex items-center justify-end gap-2">
              <Button variant="ghost" onClick={close} className="text-white/70" data-testid="lender-cancel">Cancel</Button>
              <Button onClick={save} disabled={saving} className="btn-gold rounded-lg h-11 px-6 gap-2" data-testid="lender-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Lender"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
